/**
 * The beeswarm (Design Review V-6; PRD 10.3; Phase 42): the capacity heatmap drawn as one dot
 * per row per week, so the reader can see the *distribution* a percentage hides.
 *
 * Every other reading of this response aggregates. The matrix gives a row its colour, the
 * multiples give it a line, the horizon folds it — and the company figure above them all is a
 * ratio of two sums, which is 92 per cent whether everybody is at 92 or half the team is at 60
 * and the other half at 130. Those are different weeks and they need different decisions, and
 * this is the only view here that tells them apart: the dots are the people, the company
 * aggregate is drawn over them as a rule, and the gap between the cloud and the rule is the
 * whole argument.
 *
 * What it draws:
 *
 *   - **one dot per row and week**, at the row's own `utilization` for that week — never binned,
 *     never averaged, so a dot's height is the matrix cell's value and can be read against it;
 *   - **dodged, not jittered**. Dots that would overlap are pushed sideways to the first free
 *     place on a lattice, nearest the column's centre first. Jitter would be random, and two
 *     readings of one run would be two different pictures; this is a function of the data, so
 *     the same week always draws the same swarm;
 *   - **coloured by the group the row belongs to** — the person's department at the individual
 *     grain, which is what V-6 asks for, and the row itself at every other grain (a service row
 *     belongs to no department). The assignment is made from the whole response, never from what
 *     is highlighted, so emphasising one group cannot move another group's hue;
 *   - **against the threshold bands**, which are the frame: the reader is counting dots over the
 *     overloaded line and dots under the healthy band, and both counts are stated per week.
 *
 * A week with no capacity has no ratio and therefore no dot, exactly as it has no cell colour in
 * the matrix and no point on a sparkline. Those are counted, not dropped silently.
 *
 * Every number is a field of the `GET /forecast/heatmap` cell for that row and week: this module
 * positions and counts, and computes no allocation of its own. Shared by the browser client, the
 * CSV export and the tests. Pure: no DOM, no I/O.
 */
import { col } from './table.js';
import { assignTypePalette, typeSlot } from './palette.js';
import { ROW_NOUNS, round2, thresholdScale } from './heatmap.js';
import { FOCUS_CEILING, SCALE_HEADROOM, SCALE_MODES } from './multiples.js';
import { ticks } from './scales.js';

/** The two shared y-scales, the small multiples' own: fit to the worst dot, or focus and clip. */
export const SWARM_SCALES = SCALE_MODES;

/**
 * The ceiling the `focus` scale tops out at, and this view's **default**.
 *
 * The multiples default to `fit` because a flattened sparkline is still a shape. A swarm is not:
 * the threshold bands are the frame every dot is read against, and one person at 842 per cent
 * puts the healthy band — where most of the team is — into the bottom twelfth of the plot, where
 * the bands cannot be told apart and neither can the dots. So this view clips by default, marks
 * every dot it clipped, counts them on the card, and offers `fit` for the reader who wants the
 * outlier in the same picture.
 */
export const SWARM_CEILING = FOCUS_CEILING;

/**
 * The coordinate space the dodge is computed in is the caller's: the card hands in the pixel
 * radius and the half-width of a week's column, because both come from the chart frame's own
 * measurement of the page. These are the defaults the card uses and the tests read.
 */
export const DOT = Object.freeze({ radius: 3.5, ring: 1 });

/** How many lattice places out the dodge will look before it gives up and stacks. */
const MAX_PLACES = 512;

/** What the dots are grouped and coloured by, per dimension. */
export const GROUP_BY = Object.freeze({
  individual: 'department',
  company: 'row',
  department: 'row',
  service: 'row',
  office: 'row',
});

/** What a group is called on the card, per dimension. */
export const GROUP_NOUNS = Object.freeze({
  individual: ['Department', 'departments'],
  company: ['Row', 'rows'],
  department: ['Department', 'departments'],
  service: ['Service', 'services'],
  office: ['Office', 'offices'],
});

/** The group a row with no department of its own falls into. */
export const UNGROUPED = Object.freeze({ key: '_none', label: 'No department' });

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
const hours = (v) => round2(num(v) ?? 0);

/**
 * The group a row belongs to. At the individual grain that is the person's department, which is
 * the field `GET /forecast/heatmap` carries on `entity`; everywhere else a row is its own group,
 * because a service belongs to no department and colouring every dot alike would spend the one
 * categorical channel this view has on nothing.
 *
 * @param {object} row  a heatmap row
 * @param {string} dimension
 */
export function groupOf(row, dimension) {
  if (GROUP_BY[dimension] !== 'department') return { key: row.key, label: row.label ?? row.key };
  const department = row.entity?.department ?? null;
  return department ? { key: department, label: department } : { ...UNGROUPED };
}

/**
 * The hue every group wears, assigned once from the **whole response**.
 *
 * This is PRD 7.3's rule and the reason the plan's test asks for a colour that is stable under a
 * filter: the input is every row the run returned, ranked by the hours it carries, so
 * highlighting one department, picking an entity or narrowing the window's rows cannot hand
 * another department a different hue. Seven groups take the seven validated slots and the rest
 * read as Other, which is the same cap every other categorical vocabulary here lives under.
 *
 * The groups are a third categorical vocabulary (after the project types and the ribbon's demand
 * layers) and, like the layers, they never share a screen with either — no slot means two things
 * at once in one picture.
 *
 * @param {object} data  a `GET /forecast/heatmap` response
 */
export function swarmGroups(data) {
  const dimension = data?.dimension ?? 'company';
  const byKey = new Map();
  for (const row of data?.rows ?? []) {
    const group = groupOf(row, dimension);
    const seen = byKey.get(group.key) ?? { ...group, rows: 0, hours: 0 };
    seen.rows += 1;
    seen.hours = round2(
      seen.hours + (row.cells ?? []).reduce((s, c) => s + hours(c.allocated_hours), 0),
    );
    byKey.set(group.key, seen);
  }
  const assigned = assignTypePalette(
    [...byKey.values()].map((g) => ({
      value: g.key,
      label: g.label,
      volume: g.hours,
      // The rows with no department of their own never claim a hue ahead of a real group.
      priority: g.key === UNGROUPED.key ? 0 : 1,
    })),
  );
  return assigned.map((a) => ({ ...a, ...byKey.get(a.value), key: a.value }));
}

/** Look a group up in an assignment; an unknown one reads as Other, as an unknown type does. */
export function groupSlot(groups, key) {
  return { ...typeSlot(groups, key), key };
}

/**
 * The y-scale every dot is placed on.
 *
 * `focus` runs to a fixed ceiling and clips what is past it; `fit` runs to the worst dot with a
 * little headroom, so the highest dot is a dot and not a mark welded to the top edge. Both are
 * shared by every column — comparing weeks is the point, so a per-week scale would be a lie.
 *
 * @param {Array<{ utilization: number | null }>} dots
 * @param {object} thresholds
 * @param {'fit' | 'focus'} mode
 */
export function swarmScale(dots, thresholds, mode = 'focus') {
  const t = thresholds ?? { underload_below: 0.85, caution_above: 1, overloaded_above: 1.15 };
  const ceiling = Math.max(SWARM_CEILING, round2(t.overloaded_above * 1.1));
  if (mode === 'focus') {
    const axis = ticks([0, ceiling], { count: 4 });
    return { mode, max: axis.domain[1], step: axis.step, values: axis.values };
  }
  const worst = (dots ?? []).reduce((m, d) => Math.max(m, d.utilization ?? 0), 0);
  const axis = ticks([0, Math.max(ceiling, worst * SCALE_HEADROOM)], { count: 4 });
  return { mode, max: axis.domain[1], step: axis.step, values: axis.values };
}

/** One dot: the row, the week, and the cell it stands for. */
function dotFrom(row, cell, group, thresholds) {
  const utilization = num(cell.utilization);
  const capacity = hours(cell.capacity_hours);
  return {
    key: `${row.key}|${cell.week_start}`,
    row_key: row.key,
    label: row.label ?? row.key,
    entity: row.entity ?? null,
    group: group.key,
    group_label: group.label,
    week_start: cell.week_start,
    week_end: cell.week_end ?? null,
    utilization,
    allocated_hours: hours(cell.allocated_hours),
    capacity_hours: capacity,
    headroom_hours: num(cell.headroom_hours),
    high_confidence_hours: hours(cell.high_confidence_hours),
    band: cell.band ?? null,
    has_capacity: capacity > 0,
    over: utilization !== null && utilization > thresholds.overloaded_above,
    under: utilization !== null && utilization < thresholds.underload_below,
    cell,
  };
}

/** The median of a sorted list of numbers, averaging the middle pair of an even-length one. */
function medianOf(sorted) {
  if (!sorted.length) return null;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : round2((sorted[mid - 1] + sorted[mid]) / 2);
}

/**
 * A percentile by linear interpolation between the neighbouring order statistics — the quartiles
 * the card states as the spread. Not the sampled mode's percentiles and not pretending to be:
 * this is a summary of the dots on screen, which is why it is computed here and not read from a
 * row the way Phase 40's band is.
 */
export function quantile(values, p) {
  const sorted = [...values].sort((a, b) => a - b);
  if (!sorted.length) return null;
  const at = (sorted.length - 1) * p;
  const lo = Math.floor(at);
  const hi = Math.ceil(at);
  if (lo === hi) return round2(sorted[lo]);
  return round2(sorted[lo] + (sorted[hi] - sorted[lo]) * (at - lo));
}

/**
 * What one week's dots say as figures: how many over the line, how many under the band, where
 * the middle half of the team sits, and the company's own ratio for the same week.
 *
 * The last is the point of the view. `aggregate` is the totals row — the number every other
 * screen shows — and `median` is the middle dot; a week where they are far apart is a week the
 * aggregate is describing badly.
 */
export function weekStats(dots, total, thresholds) {
  const drawn = dots.filter((d) => d.utilization !== null);
  const values = drawn.map((d) => d.utilization);
  const sorted = [...values].sort((a, b) => a - b);
  const p25 = quantile(values, 0.25);
  const p75 = quantile(values, 0.75);
  return {
    dots: drawn.length,
    missing: dots.length - drawn.length,
    over: drawn.filter((d) => d.over).length,
    under: drawn.filter((d) => d.under).length,
    healthy: drawn.filter((d) => !d.over && !d.under).length,
    min: sorted.length ? round2(sorted[0]) : null,
    max: sorted.length ? round2(sorted.at(-1)) : null,
    median: medianOf(sorted),
    p25,
    p75,
    spread: p25 === null || p75 === null ? null : round2(p75 - p25),
    aggregate: num(total?.utilization),
    aggregate_band: total?.band ?? null,
    allocated_hours: hours(total?.allocated_hours),
    capacity_hours: hours(total?.capacity_hours),
    unassigned_hours: hours(total?.unassigned_hours),
    thresholds,
  };
}

/**
 * Everything the swarm draws: a column per week, its dots, its figures, the groups' hues and the
 * shared scale.
 *
 * @param {object} data  a `GET /forecast/heatmap` response
 * @param {{ scale?: 'fit' | 'focus' }} [opts]
 */
export function swarmModel(data, { scale = 'focus' } = {}) {
  const totals = data?.totals ?? [];
  if (!totals.length) return null;
  const dimension = data.dimension ?? 'company';
  const thresholds = data.thresholds ?? {
    underload_below: 0.85,
    caution_above: 1,
    overloaded_above: 1.15,
  };
  const groups = swarmGroups(data);
  const rows = (data.rows ?? []).map((row) => ({ row, group: groupOf(row, dimension) }));
  const columns = totals.map((total, index) => {
    const dots = [];
    for (const { row, group } of rows) {
      const cell = (row.cells ?? [])[index];
      if (!cell || cell.week_start !== total.week_start) continue;
      dots.push(dotFrom(row, cell, group, thresholds));
    }
    return {
      index,
      week_start: total.week_start,
      week_end: total.week_end ?? null,
      dots,
      stats: weekStats(dots, total, thresholds),
      total,
    };
  });
  const all = columns.flatMap((c) => c.dots);
  return {
    weeks: columns.map((c) => c.week_start),
    columns,
    groups,
    dots: all,
    // Every row of the response is one dot in every week of it, less the weeks with no ratio to
    // place: the plan's count, stated so a test and a reader can check it the same way.
    expected: rows.length * totals.length,
    drawn: all.filter((d) => d.utilization !== null).length,
    missing: all.filter((d) => d.utilization === null).length,
    rows: rows.length,
    scale: swarmScale(all, thresholds, scale),
    thresholds,
    dimension,
    withheld: data.withheld_count ?? 0,
  };
}

/**
 * Where the dots of one column sit, sideways.
 *
 * The rule is the beeswarm's: take the dots in order up the axis and give each one the place
 * nearest the column's centre that no dot already placed is touching — 0, then one lattice step
 * right, then one left, and outward. It is a function of the values alone, so the picture is
 * reproducible: the same week of the same run draws the same swarm on every machine, which
 * random jitter would not.
 *
 * Ties are broken by the dot's key, so two people at exactly the same utilization are always in
 * the same order and never swap places between renders.
 *
 * A week crowded enough that its swarm would reach into the next column is **compressed** rather
 * than spilled: every offset is scaled by whatever brings the widest back inside the column, the
 * dots keep their size and their heights, and the column is flagged so the card can say that the
 * dots are now touching because the week is full, not because two people are alike.
 *
 * @param {Array<{ y: number, key: string }>} items  dots with their pixel y
 * @param {{ radius?: number, halfWidth?: number, step?: number }} [opts]
 */
export function dodge(items, { radius = DOT.radius, halfWidth = Infinity, step = radius } = {}) {
  const sorted = [...(items ?? [])].sort(
    (a, b) => a.y - b.y || String(a.key).localeCompare(String(b.key)),
  );
  const placed = [];
  const reach = (2 * radius) ** 2;
  for (const item of sorted) {
    let offset = 0;
    for (let place = 0; place < MAX_PLACES; place += 1) {
      // 0, +1, -1, +2, -2 … lattice steps out from the centre.
      offset = (place % 2 === 1 ? 1 : -1) * Math.ceil(place / 2) * step;
      const hit = placed.some((p) => (p.y - item.y) ** 2 + (p.offset - offset) ** 2 < reach - 1e-9);
      if (!hit) break;
    }
    placed.push({ ...item, offset });
  }
  const widest = placed.reduce((m, p) => Math.max(m, Math.abs(p.offset)), 0) + radius;
  const compressed = Number.isFinite(halfWidth) && widest > halfWidth && widest > 0;
  const factor = compressed ? (halfWidth - radius) / (widest - radius || 1) : 1;
  return {
    dots: placed.map((p) => ({ ...p, offset: round2(p.offset * factor) })),
    compressed,
    widest: round2(widest),
    factor: round2(factor),
  };
}

/**
 * The columns placed in the frame's own coordinates: one dot per row and week at its cell's
 * utilization, dodged inside the week's band, with the dots past the top of the scale clamped
 * to it and flagged rather than quietly dropped off the plot.
 *
 * @param {ReturnType<typeof swarmModel>} model
 * @param {{ x: Function, y: Function, radius?: number }} scales  `x` a band scale, `y` linear
 */
export function swarmGeometry(model, { x, y, radius = DOT.radius }) {
  const max = model.scale.max;
  const halfWidth = x.bandwidth() / 2;
  return model.columns.map((column) => {
    const drawable = column.dots.filter((d) => d.utilization !== null);
    const placed = dodge(
      drawable.map((d) => ({
        key: d.key,
        dot: d,
        clipped: d.utilization > max,
        y: round2(y(Math.min(d.utilization, max))),
      })),
      { radius, halfWidth },
    );
    const centre = x.centre(column.week_start);
    return {
      ...column,
      x: x(column.week_start),
      centre,
      width: x.bandwidth(),
      compressed: placed.compressed,
      dots: placed.dots.map((p) => ({
        ...p.dot,
        clipped: p.clipped,
        x: round2(centre + p.offset),
        y: p.y,
        offset: p.offset,
      })),
      clipped: placed.dots.filter((p) => p.clipped).length,
      aggregate_y:
        column.stats.aggregate === null ? null : round2(y(Math.min(column.stats.aggregate, max))),
      aggregate_clipped: column.stats.aggregate !== null && column.stats.aggregate > max,
    };
  });
}

/** The threshold bands as the plot's background, in the frame's coordinates. */
export function swarmBackground(scale, thresholds, y) {
  const states = thresholdScale(thresholds);
  const bands = [];
  let from = 0;
  for (const state of states) {
    const to = state.max === null ? scale.max : Math.min(scale.max, state.max);
    if (to > from)
      bands.push({
        key: state.key,
        label: state.label,
        icon: state.icon,
        range: state.range,
        tint: state.tint,
        token: state.var,
        from,
        to,
        y: round2(y(to)),
        height: round2(y(from) - y(to)),
      });
    from = to;
    if (from >= scale.max) break;
  }
  return bands;
}

/** The figures the card's header states. */
export function swarmSummary(model) {
  const columns = model?.columns ?? [];
  let worstOver = null;
  let widest = null;
  for (const column of columns) {
    if (worstOver === null || column.stats.over > worstOver.stats.over) worstOver = column;
    if (
      column.stats.spread !== null &&
      (widest === null || column.stats.spread > widest.stats.spread)
    )
      widest = column;
  }
  return {
    weeks: columns.length,
    rows: model?.rows ?? 0,
    dots: model?.drawn ?? 0,
    missing: model?.missing ?? 0,
    clipped: (model?.dots ?? []).filter(
      (d) => d.utilization !== null && d.utilization > model.scale.max,
    ).length,
    groups: model?.groups?.length ?? 0,
    worst_over: worstOver,
    widest_spread: widest,
    // A week whose aggregate sits outside the middle half of its own dots is a week the company
    // figure describes badly, which is the case this view exists to make.
    misleading: columns.filter(
      (c) =>
        c.stats.aggregate !== null &&
        c.stats.p25 !== null &&
        (c.stats.aggregate < c.stats.p25 || c.stats.aggregate > c.stats.p75),
    ).length,
  };
}

/** The sentence a dot says, for the tooltip's footer and the hit area's screen-reader text. */
export function dotText(dot, thresholds) {
  if (dot.utilization === null) return 'no capacity that week — no ratio to place';
  const where = dot.over
    ? `over ${Math.round(thresholds.overloaded_above * 100)}%`
    : dot.under
      ? `under ${Math.round(thresholds.underload_below * 100)}%`
      : 'inside the healthy band';
  return `${Math.round(dot.utilization * 100)}% — ${where}`;
}

/**
 * The table view's rows: one per dot, the same numbers the dots are placed from, so the gate's
 * "count the dots over the line and compare with the matrix" can be done from the export rather
 * than by eye.
 */
export function swarmExportRows(model) {
  const out = [];
  for (const column of model?.columns ?? [])
    for (const dot of column.dots)
      out.push({
        row: dot.label,
        key: dot.row_key,
        group: dot.group_label,
        week_start: dot.week_start,
        utilization: dot.utilization,
        band: dot.band,
        allocated_hours: dot.allocated_hours,
        capacity_hours: dot.capacity_hours,
        headroom_hours: dot.headroom_hours,
        high_confidence_hours: dot.high_confidence_hours,
        week_median: column.stats.median,
        week_p25: column.stats.p25,
        week_p75: column.stats.p75,
        week_over: column.stats.over,
        week_under: column.stats.under,
        week_aggregate: column.stats.aggregate,
      });
  return out;
}

// ---------------------------------------------------------------------------------------------
// Table columns (Phase 37)
//
// A specification, not a rendering: the screen decorates it with `withFormats` and the CSV
// writer reads the same list, so an export cannot hold a different set of columns from the
// table it came from.

/** @param {{ dimension?: string }} opts */
export function swarmColumns({ dimension = 'company' } = {}) {
  return [
    col.text('row', (ROW_NOUNS[dimension] ?? ROW_NOUNS.company)[0]),
    col.text('group', (GROUP_NOUNS[dimension] ?? GROUP_NOUNS.company)[0]),
    col.week('week_start', 'Week', { type: 'text' }),
    col.percent('utilization', 'Allocation'),
    col.text('band', 'State', { csv: (v) => v ?? '' }),
    col.hours('allocated_hours', 'Allocated'),
    col.hours('capacity_hours', 'Capacity'),
    col.hours('headroom_hours', 'Headroom'),
    col.hours('high_confidence_hours', 'High confidence'),
    col.percent('week_median', 'Week median'),
    col.percent('week_p25', 'Week 25th'),
    col.percent('week_p75', 'Week 75th'),
    col.count('week_over', 'Over the line'),
    col.count('week_under', 'Under the band'),
    col.percent('week_aggregate', 'Company'),
  ];
}
